{
  "en": {
    "aiChat": {
      "aiAssistant": {
        "explore": {
          "filters": {
            "all": "Highlights",
            "character": "Characters",
            "model": "AI Models",
            "plugin": "Plugins",
            "pupils": "Students",
            "teachers": "Educators"
          },
          "titles": {
            "administration": "School Administration and Organization",
            "exercises": "Practice Exercises and Tutoring",
            "featured": "Popular Assistants",
            "feedback": "Feedback and Grading",
            "information": "Information and Orientation",
            "inspiration": "Inspiration and Creativity",
            "material": "Teaching Materials and Exercises",
            "model": "Plugins",
            "other": "Other",
            "planning": "Lesson Planning and Organization",
            "roleplay": "Character Chatbots"
          }
        },
        "errors": {
          "skillUploadFailed": "Skill upload failed. Please try again.",
          "maxSkillsReached": "Maximum {max} skills per assistant.",
          "saveFailed": "Changes could not be saved. Please try again.",
          "skillContentTooLong": "The skill content exceeds the maximum length of 20,000 characters.",
          "skillBundleInvalid": "The skill bundle is invalid or contains unsafe file paths.",
          "skillBundleMissingSkill": "The skill bundle must contain a SKILL.md in the root directory or in a single parent folder.",
          "skillBundleTooLarge": "The skill bundle or a contained file exceeds the allowed size.",
          "skillBundleTooManyFiles": "The skill bundle may contain at most 200 files."
        },
        "hints": {
          "fileExtractingText": "Extracting text...",
          "fileParsingImages": "Parsing images: {progress}%",
          "fileIndexingFailed": "File indexed failed",
          "fileIndexingRunning": "File indexed in progress",
          "fileIndexingProgress": "Indexing: {progress}%",
          "fileIndexingSuccessfully": "File indexed successfully",
          "largeFileUploaded": "The uploaded text is very large and is well suited as background knowledge for specific questions about the text and reproduction tasks. For queries that require transfer work and are intended to establish logical connections across the entire document, your assistant will not perform at its best and will only process the text in excerpts. For this type of complex enquiry, we recommend uploading files up to a maximum of 4 A4 pages of text.",
          "maxHandoffsReached": "Maximum {max} handoff assistants per assistant.",
          "noHandoffAssistants": "No matching assistants found.",
          "skillDocLink": "Learn more",
          "skillUploadLimits": "(max. {max} skills, {formats}, max. {size} MB each)",
          "skillsFormatsBundle": "Upload a Markdown file (.md) or a ZIP bundle containing a SKILL.md.",
          "skillsIntro": "Skills are work instructions that your assistant loads as needed, e.g., a grading rubric or a fixed procedure for tasks."
        },
        "infoMessages": {
          "assistantFiles": "Here you can upload additional background knowledge for your assistant to access in a request. Please note that more complex requests can be processed better with files up to a maximum of 4 A4 pages.",
          "conversationStarters": "Provide a phrase or question to prompt users to start interacting with the assistant.",
          "enabledTools": "Your assistant can call up one plugin per request and decides on a single one for each prompt.",
          "handoffAssistants": "Select up to two assistants that this assistant may delegate work to during a conversation.",
          "skillsSection": "Skills are instructions that your assistant loads as needed. They belong to this assistant.",
          "systemPrompt": "Please note that a very long instruction will prolong the processing of your request. We recommend that the instructions are no longer than 3 A4-pages, i.e. approx. 1,200 words."
        },
        "labels": {
          "author": "Author",
          "enabledTools": "Plugins",
          "handoffAssistants": "Handoff Assistants",
          "model": "Model",
          "openModels": "Open Models",
          "otherModels": "Other Models",
          "recommendedModels": "Recommended Models",
          "remixCount": "Remix",
          "skillFileCount": "1 file | {count} files",
          "systemPrompt": "Role and instructions"
        },
        "links": {
          "create": "Create an assistant",
          "downloadSkill": "Download skill",
          "removeSkill": "Remove skill",
          "replaceSkill": "Replace file",
          "uploadSkill": "Upload skill"
        },
        "paragraphs": {
          "alert": "Whether for you or your students, your own assistant is focussed on a specific area of application. For example, you can upload documents such as images or newspaper articles and set specific tasks.",
          "alertPupil": "Your own assistant is focussed on a specific area of application. For example, you can upload documents such as images or newspaper articles and set specific tasks."
        },
        "placeholders": {
          "conversationStarter": "Enter a suggested question or prompt...",
          "description": "Brief explanation for users of what your assistant can do...",
          "searchHandoffAssistants": "Search assistants for handoff...",
          "systemPrompt": "Define exactly how your assistant should behave in which situations, which plugins and sources they should prefer to use in which cases and which role they should take on in the chat."
        },
        "titles": {
          "alert": "You can easily create your own assistant",
          "community": "Discover AI assistants for your everyday school life",
          "create": "Create and share your own assistants"
        },
        "tooltips": {
          "clickToUploadAvatar": "Click or drag to upload avatar",
          "generateAvatar": "Generate avatar",
          "generateConversationStarter": "Generate a conversation starter suggestion",
          "generateConversationStarterDisabled": "Fill in name and description to generate a conversation starter for your assistant",
          "generatePrompt": "Generate instruction",
          "adminOnly": "Only visible for admins 🤓",
          "generatePromptDisabled": "Fill in name and description to generate an instruction for your assistant",
          "remixNotAllowed": "This assistant does not allow remixes."
        }
      },
      "confirm": {
        "deleteAssistant": {
          "text": "The assistant will be removed from your overview. You can find it in the trash afterwards.",
          "title": "Delete assistant?"
        },
        "deleteChat": {
          "text": "This action cannot be undone. The chat {modelName} will be permanently deleted along with all messages.",
          "title": "Delete chat?"
        },
        "deleteSkill": {
          "title": "Delete skill “{title}”?",
          "text": "The skill will be removed from this assistant. This cannot be undone."
        }
      },
      "conversationGroups": {
        "last30Days": "Previous 30 days",
        "last7Days": "Previous 7 days",
        "older": "Older",
        "today": "Today",
        "yesterday": "Yesterday"
      },
      "conversationStartersDefault": {
        "prompt1": "Create a brainstorming session with five creative ideas for introducing the topic of \"electrical resistance\" in physics for a 9th grade class.",
        "prompt2": "Develop three open-ended exam questions on character analysis in \"The Visit\" for an 11th grade German course at a comprehensive school.",
        "prompt3": "Write an invitation to a parent-teacher meeting on September 25 at 6:30 p.m.",
        "prompt4": "Create a tabular lesson plan for a 90-minute 8th grade mathematics lesson on the topic of \"Pythagorean theorem\"."
      },
      "conversationStartersStudents": {
        "prompt1": "Help me understand what to do: Write a literary analysis of the poem.",
        "prompt2": "Can you explain the term \"cacophony\" to me?",
        "prompt3": "I need to prepare an essay on the impact of the industrial revolution on society. Can you help me?",
        "prompt4": "Help me study for a geography exam on the topic of causes and effects of freshwater pollution."
      },
      "errors": {
        "conversationCreateFailed": "Failed to create conversation. Please try again.",
        "fileAlreadyExists": "File '{filename}' already uploaded",
        "fileRemoveFailed": "Failed to remove file",
        "fileUploadFailed": "Failed to upload '{filename}'",
        "maxFilesReached": "Maximum {max} files allowed",
        "visionNotSupportedDescription": "Please switch the model or remove the image.",
        "visionNotSupportedTitle": "The selected model does not support images."
      },
      "form": {
        "avatar": "What does your assistant look like?",
        "fileLimit": "(max. {maxFiles} files, each max. {size} MB, total max. {totalSize} MB)",
        "files": "Does your assistant have any special background information?",
        "imageHint": "Auto-generate an image with AI or {uploadLink}.",
        "knowledge": "Who is your assistant and what can they do?",
        "skills": "Which skills should your assistant master?",
        "title": "Create your own assistant",
        "upload": "Upload background knowledge",
        "uploadLinkText": "upload your own preview image"
      },
      "headers": {
        "editConversation": "Rename conversation"
      },
      "help": {
        "scrollToBottom": "Scroll to bottom",
        "scrollToTop": "Scroll to top",
        "startSpeech": "Read aloud",
        "stopSpeech": "Stop reading",
        "switchGoogleAccount": "Switch Google Account",
        "uploadFileDescription": "Supported formats: PDF, Word, PowerPoint, Excel, CSV. Important: Make sure the files do not contain any personal data.",
        "uploadGoogleDrive": "Connect to Google Drive",
        "uploadImageDescription": "Ask questions about this image, have it analyzed, or transcribe text content. Important: Make sure the images do not contain any personal data.",
        "voiceMode": "Interactive Voice Model 🤓",
        "voiceRecorder": "Start audio recording and record your prompt.",
        "documentExport": "Find new export functions here",
        "exportHint": "Find new export options here, e.g. as PDF, PowerPoint or Word file."
      },
      "hints": {
        "cancelled": "Response cancelled.",
        "chatPrompt": "Never share personal data with the AI and check the answers critically.",
        "chatPromptMore": "Learn more",
        "emptyConversationPdf": "This conversation doesn't contain any messages.",
        "emptyConversationPdfTitle": "Download AI chat conversation",
        "emptyConversationPdfAlt": "Illustration of an empty chat conversation",
        "fileUpload": "Upload files",
        "fileUploadLimitReached": "You have reached the upload limit for files and images. Remove a file to add a new one.",
        "flagged": "This message was rejected due to content filters. Please try again with a different prompt.",
        "noSearchResults": "We couldn't find a chat with this term. ",
        "perplexityNote": "The answers provided by this AI chatbot are based on a web search conducted by Perplexity. Please note that the results are based on web content and should therefore be critically reviewed. Use the web search to ask your questions and carefully check the sources.",
        "remixableHint": "Others can copy your assistant and use it as a basis for their own assistants.",
        "selectSystemHint": "Settings and tools"
      },
      "labels": {
        "allCitationsWithCount": "All Citations ({count})",
        "category": "Category",
        "conversationStarter": "Chat Starter",
        "conversationStarters": "Chat Starters",
        "createdWithModel": "Created with {model}",
        "experimental": "Preview",
        "featured": "Featured Flag",
        "remixable": "Allow remixes",
        "fobizzAi": "fobizz AI",
        "imageModel": "Image model",
        "international": "Language:",
        "maxDailyMessagesCount": "Daily message limit",
        "model": "Language model",
        "newConversation": "New conversation",
        "remixAssistantFiles": "The documents may be reused when remixing.",
        "removeSystemHint": "Remove system hint",
        "selectModel": "Select language model",
        "modelSelectionGuide": "Which language model is the best fit for your assistant?",
        "modelSelectionLink": "View model list",
        "pupilRestrictedModelWarning": "This model is available to teachers only. Students automatically use a different default model.",
        "selectSystemHint": "Select a system hint",
        "showAllCitations": "Show all citations",
        "subcategory": "Subcategory",
        "thinking": "Thinking",
        "handoff": "Handoff",
        "viewImage": "View image",
        "message": "Message from {author}"
      },
      "toolApproval": {
        "approve": "Approve",
        "reject": "Reject",
        "approved": "Approved",
        "rejected": "Rejected",
        "error": "The decision could not be saved. Please try again."
      },
      "runActivity": {
        "outcome": {
          "completed": "Done",
          "cancelled": "Cancelled",
          "failed": "Error"
        },
        "status": {
          "running": "Running",
          "awaitingApproval": "Awaiting your confirmation",
          "completed": "Completed",
          "cancelled": "Cancelled",
          "failed": "Failed"
        },
        "steps": "1 step | {count} steps",
        "step": "Step {n}",
        "count": "{count}×",
        "stepCancelled": "Cancelled",
        "stepFailed": "This step has failed.",
        "summary": "{outcome} after {duration}, {steps}",
        "announceStep": "{tool} completed",
        "duration": {
          "lessThanSecond": "< 1 s",
          "seconds": "{s} s",
          "minutes": "{m} min",
          "minutesSeconds": "{m} min {s} s",
          "hoursMinutes": "{h} hr {m} min"
        },
        "runtimeTools": {
          "search_tools": "Find Tools",
          "read_tool_result": "Read more"
        },
        "unknownTool": "Step",
        "waitingForApproval": "Waiting for your confirmation",
        "handoffTo": "at {name}"
      },
      "links": {
        "connectGoogleDrive": "Google Drive 🤓",
        "explore": "Assistants Catalog",
        "helpModelCards": "Help with language models",
        "moreModels": "More models",
        "myAssistants": "My assistants",
        "removeFile": "Remove file",
        "removeMaterial": "Remove material",
        "startChat": "Start chat",
        "switchGoogleAccount": "Switch account",
        "uploadFile": "Upload file",
        "uploadGoogleDrive": "Select file",
        "uploadImage": "Upload image"
      },
      "modelDescriptions": {
        "0": "Automatically chooses a suitable model for your request",
        "1": "For quick responses and simple dialogues",
        "2": "For multi-step tasks and complex problems"
      },
      "placeholders": {
        "promptInputDefault": "How can I help you today?",
        "assistantDeleted": "The assistant was deleted — this conversation is read-only.",
        "searchChats": "Search chats...",
        "searchMyAssistants": "Search your assistants..."
      },
      "promptLibraryModal": {
        "description": "Our prompt collection contains many useful ideas for everyday school life. Choose a prompt to adopt it directly.",
        "header": "Prompt Collection",
        "submitPromptHtml": "Contribute prompts: <a href=\"https://app.fobizz.com/surveys/surveys/92d3fdd2-7e71-4328-896e-0c9ff60e0ca0/public_survey_responses/new?token=55558b27f282cbc15eafe58d1c9e8865\" target=\"_blank\">Submit here</a>"
      },
      "studentPromptLibrary": {
        "examPrep": {
          "prompts": [
            {
              "title": "Creating a Study Plan",
              "prompt": "I'm a student in [8th grade] at a [district school]. In 5 days I will write an exam in the subject [Politics] on the topic [The US Electoral College] Act like an experienced study coach who supports me in preparing optimally for the exam. Ask me questions so you can create a good study plan with concrete times for me."
            },
            {
              "title": "Quizzing",
              "prompt": "I'm a student in [10th grade] at a [high school]. Act like a patient and competent teacher for the subject [Music]. Quiz me on [Forms and genres in the Baroque period] and refer to the curriculum. Your goal is to check whether I have understood the content and can actively reproduce it. Ask me the questions individually. Wait until I have answered, then give me feedback. If I haven't answered the question sufficiently, rephrase it. Otherwise, ask another question to check my knowledge of the topic."
            }
          ],
          "title": "Exam Preparation"
        },
        "jobApplication": {
          "prompts": [
            {
              "title": "Career Counseling",
              "prompt": "I want you to act as a friendly and competent career counselor. I am a person seeking guidance in their professional life, and your task is to help me figure out which professions are best suited to me based on my skills, interests, and experiences. You should also consider the various options, explain trends in the job market in various industries, and give advice on which qualifications are advantageous for practicing certain professions. If you don't have enough information, ask for more details. Ask me first: Hello, how can I help you?"
            },
            {
              "title": "Job Interview",
              "prompt": "I want you to act as a friendly, empathetic and competent HR manager conducting a job interview. I am an applicant for the position [Apprentice as a motor vehicle mechatronics technician] and you will ask me the questions for the job interview for the position. During the conversation, focus on my motivation, my educational career, my personal competences. Ask me the questions one after the other, as an interviewer does, and wait for my answers. If I answer very briefly, ask follow-up questions and respond to my answer if it serves the course of the conversation. Don't write explanations. If you don't have enough information, ask for more details. My first sentence is: Hello and thank you for inviting me to the interview."
            },
            {
              "title": "Writing a Resume",
              "prompt": "I want you to act as a friendly and competent career counselor who helps me write my resume. The resume should present me as serious, friendly and competent and be written and structured according to current standards. If you don't have enough information, ask for more details. Ask me first: Hello, which position would you like to apply for?"
            }
          ],
          "title": "Job Application Training"
        },
        "learningAssistance": {
          "prompts": [
            {
              "title": "Simplify a Topic",
              "prompt": "I'm a student in [7th grade] at a [middle school]. Act like a patient and competent teacher for the subject [Physics]. Explain the topic [Structure and components of simple electrical circuits] to me in the simplest possible way, with practical examples and in a way that I can easily remember. After you've given the explanation, ask me if I want more examples or a different explanation."
            },
            {
              "title": "Dive into a Topic",
              "prompt": "I'm a student in [6th grade] at a [secondary school]. Act like a patient and competent teacher for the subject [Geography]. Explain to me according to the Pareto Principle the 20% most important points about the topic [Causes of day and night and ebb and flow], so that I understand 80% of the topic well and easily. Use practical examples and explain in a way that I can easily remember. After you've given the explanation, ask me if I want more examples or a different explanation."
            },
            {
              "title": "I Didn't Understand the Task",
              "prompt": "I'm a student in [4th grade] at an [elementary school]. Act like a patient and competent teacher for the subject [Mathematics]. I didn't understand a task. Analyze the task carefully and paraphrase it in simple words. Then help me solve the task by guiding me step by step through the solution path. Don't give me solutions, but ask me questions so you know how to help me. If you don't have enough information, ask for more details. Start with the question: Hello, which task do you need help with?"
            },
            {
              "title": "I Didn't Understand a Topic",
              "prompt": "I'm a student in [9th grade] at a [comprehensive school]. Act like a patient and competent teacher for the subject [History]. I didn't understand the topic [Causes of the American Civil War]. Ask me questions so you know how to help me. Help me to better understand the topic step by step with small, simple explanations and simple comprehension questions. If you don't have enough information, ask for more details."
            },
            {
              "title": "Problem Solving",
              "prompt": "I'm a student in [4th grade] at an [elementary school]. Act like a patient and competent teacher for the subject [Mathematics]. Explain to me how to solve a certain task step by step and as simply as possible. Don't give me solutions for tasks, but point out my thinking errors and give me tips and clues on how I can tackle the task myself. If you don't have enough information, ask for more details. Start with the question: Hello, which task do you need help with?"
            },
            {
              "title": "Understanding Literature",
              "prompt": "I'm a student in [10th grade] at a [comprehensive school]. Act like a patient and competent teacher with extensive knowledge of world literature. I need help understanding what a certain passage of a text means in the context of the work. If you don't have enough information, ask for more details. Start by asking me: Hello, which text can I help you with?"
            }
          ],
          "title": "Learning Assistance"
        },
        "writingAssistance": {
          "prompts": [
            {
              "title": "Language Teaching",
              "prompt": "I'm a student in [6th grade] at a [middle school]. Act like a patient and competent teacher for the subject [French]. I want to improve my [French]. Please check my text and give me feedback and specific tips on how I can improve my grammar, vocabulary and writing style. Keep your answers short. Start with the question: Hello, which text can I help you with?"
            },
            {
              "title": "Writing Essays",
              "prompt": "I want you to act as a writing tutor. I'm a student who needs help improving their writing. Your task is to give me feedback on how I can improve my essay. You should also use your rhetorical knowledge and experience regarding effective writing techniques to suggest how I can better express my thoughts and ideas in written form. If you don't have enough information, ask for more details. Start with the question: Hello, which text can I help you with?"
            },
            {
              "title": "Proofreading",
              "prompt": "I want you to act as a proofreader. I will provide you with a text that you should check for spelling, grammar, and punctuation errors. Create a list with the columns: Error, Correction and Error Type and then output the corrected version of the text in which all corrected words are marked in bold. Start with the question: Hello, which text can I help you with?"
            }
          ],
          "title": "Writing Assistance"
        }
      },
      "success": {
        "fileRemoved": "'{filename}' removed successfully",
        "fileUploaded": "'{filename}' uploaded successfully",
        "skillRemoved": "Skill removed successfully"
      },
      "systemPrompt": "You are a helpful and friendly assistant. If you don't know an answer, say: \"I don't know.\" Ask for more information if you need it to answer my request well. Always answer in the language you are addressed in.",
      "systemPromptLibrary": {
        "careerCounseling": {
          "prompts": [
            {
              "title": "Career Counselor",
              "prompt": "I would like you to act as a career counselor. I am a person looking for guidance in my professional life and your job is to help me find out which careers are best suited to me based on my skills, interests and experience. You should also consider the different opportunities, explain the trends in the labor market in different industries and give advice on which qualifications are beneficial for pursuing certain careers. Ask me first: \"Hello, how can I help you?\" If you don't have enough information, ask for more details."
            },
            {
              "title": "Talent Coach",
              "prompt": "I would like you to act as an application trainer for young professionals. I give you a job title and you suggest what should be included in a CV for this title, as well as some questions that the applicant should be able to answer. My first job title is {enter the job title here}. If you don't have enough information, ask for more details."
            },
            {
              "title": "Job interview trainer",
              "prompt": "Imagine we are in a job interview. You take on the role of the HR manager conducting the interview. I will be the applicant and you will ask me the questions for the interview for the job as {enter the job title here}. I want you to answer only in the role of the hiring manager. Don't write the whole conversation at once. I only want you to do the interview with me. Ask me the questions and wait for my answers. Don't write explanations. Ask me the questions one by one, like an interviewer does, and wait for my answers. My first sentence is \"Good afternoon\". If you don't have enough information, ask for more details."
            }
          ],
          "title": "Career Counseling"
        },
        "experts": {
          "prompts": [
            {
              "title": "Historian",
              "prompt": "I want you to act as a historian. You research and analyze cultural, economic, political and social events in the past, collect data from primary sources and develop theories about what happened in different periods of history. My first suggestion is: \"I need help uncovering facts about {enter your topic}.\" If you don't have enough information, ask for more details."
            },
            {
              "title": "English Translator and Improver",
              "prompt": "I want you to act as an English translator that corrects and improves spelling. I will speak to you in any language, and you will recognize the language, translate it and reply in the corrected and improved version of my text in English. I want you to replace my simplified A0 level words and phrases with more beautiful and elegant higher level English words and phrases. Keep the meaning, but make the language more literary and as spoken by a native speaker. I want your answer to only contain the correction, the improvements and nothing else, don't write explanations. Ask me first: \"Hello, how can I help you today?\" If you don't have enough information, ask for more details."
            },
            {
              "title": "Math Teacher",
              "prompt": "I want you to take on the role of a Math teacher. I will give some mathematical equations or concepts and it will be your job to explain them in easy-to-understand terms. You could do this by giving step-by-step instructions on how to solve a problem, demonstrating different techniques using pictures or walking me through the solution step by step. Don't give solutions to tasks, but point out errors in thinking and give me tips and clues on how to find the solution myself. My first request is: \"I need help to understand how {enter your topic here} works.\" If you don't have enough information, ask for more details."
            }
          ],
          "title": "Experts"
        },
        "languageAndWritingCoaches": {
          "prompts": [
            {
              "title": "AI Writing Tutor",
              "prompt": "I would like you to act as a writing tutor. I am a student who needs help with improving my writing. Your job is to give me feedback on how I can improve my essay. I also want you to use your rhetorical knowledge and experience with effective writing techniques to give me suggestions on how to better express my thoughts and ideas in writing. My first request is: \"I need someone to help me revise my text\". If you don't have enough information, ask."
            },
            {
              "title": "Essay Writer",
              "prompt": "I want you to write an essay. I want you to research a given topic, formulate a thesis, and write a persuasive essay that is both informative and engaging. My first suggestion is: \"I need help writing a persuasive essay on {enter your topic here}\". If you don't have enough information, ask for more details."
            },
            {
              "title": "Storyteller",
              "prompt": "I want you to be a storyteller. You will come up with entertaining stories that are captivating, funny, entertaining and engaging. These can be fairy tales, educational stories or any other type of story that has the potential to capture people's attention and imagination. Depending on your target audience, you can choose specific topics for your storytime, e.g. you can talk about animals when addressing children; for adults, stories from history are more suitable, etc. My first request is: \"I need an interesting story about {enter your topic here}.\" If you don't have enough information, ask for more details."
            },
            {
              "title": "Poet",
              "prompt": "I want you to act as a poet. You will write poems that evoke emotion and have the power to touch people's souls. Write about the topic I give you and make sure that your words convey the feeling you want to express in a poetic, linguistically sophisticated and meaningful way. You can also come up with short verses that are still powerful enough to leave an impression in the reader's mind. My first request is, \"I need a poem about {insert your topic here}. It should include the following words, among others: {optional: enter more words here}\". If you don't have enough information, ask for more details."
            },
            {
              "title": "Proofreader",
              "prompt": "I would like to use you as a proofreader. I will provide you with texts to check for spelling, grammar and punctuation errors. Once you have finished checking the text, let me know of any necessary corrections or suggestions to improve the text. If you don't have enough information, ask for more details. If you don't have enough information, ask for more details."
            }
          ],
          "title": "Language and Writing Coaches"
        }
      },
      "systemPromptModal": {
        "editLabel": "Edit role description",
        "heading": "Change AI Assistant role",
        "useSystemPromptLibrary": "or choose from prompt library"
      },
      "takeNote": {
        "bullet1": "Sometimes I provide answers that sound plausible but are simply fabricated and wrong.",
        "bullet2": "My database is limited - I am currently not up to date on anything that has happened since 2023.",
        "heading": "Please note"
      },
      "teaserItem": {
        "linkName": "Discover training courses",
        "title": "Would you like to learn more about AI assistants?",
        "url": "https://app.fobizz.com/fortbildungen?search%5Bquery%5D=ai+assistants&search%5Bcourse_type%5D=&search%5Border%5D=most_recent&search%5Blanguage_list%5D=&search%5Blanguage_list%5D=en&commit=Apply+filter"
      },
      "teaserItem2": {
        "linkName": "Submit idea",
        "title": "Have you created an assistant you'd like to see published here?",
        "url": "https://app.fobizz.com/surveys/surveys/35e6d465-3d37-4a5e-bcc8-33a5a1e32a8a/public_survey_responses/new?token=5a50b6bfd27c7bc288159d941bf7c845"
      },
      "teaserItem3": {
        "linkName": "More information in the help section",
        "title": "Do you need help choosing a model?",
        "url": "https://app.fobizz.com/en/help/articles/445-ai-models-overview"
      },
      "tips": {
        "bullet1": "My answers are usually great first drafts, but rarely ever serve as a perfect result.",
        "bullet2": "If you are not satisfied with my answer, formulate your prompt more precisely and try again.",
        "heading": "My advice for you"
      }
    }
  }
}
